/**
 * Currency Configuration
 *
 * Configure the base currency for the application.
 * This affects all pricing displays throughout the UI.
 */

export interface TopupConfig {
  /** Quick-select preset amounts (in major currency units) */
  presets: number[];
  /** Minimum top-up amount */
  min: number;
  /** Maximum top-up amount */
  max: number;
  /** Default auto top-up trigger threshold (in major currency units) */
  defaultThreshold: number;
  /** Default auto top-up amount (in major currency units) */
  defaultAmount: number;
}

export interface CurrencyConfig {
  /** Currency code (e.g., 'USD', 'EUR', 'GBP') */
  code: string;
  /** Currency symbol (e.g., '$', '€', '£') */
  symbol: string;
  /** Currency name (e.g., 'US Dollar', 'Euro', 'British Pound') */
  name: string;
  /** Number of decimal places to display */
  decimalPlaces: number;
  /** Locale for number formatting (e.g., 'en-US', 'en-GB', 'de-DE') */
  locale: string;
  /** Symbol position: 'before' or 'after' */
  symbolPosition: 'before' | 'after';
  /** Top-up configuration for this currency */
  topupConfig: TopupConfig;
}

/**
 * Supported currency codes
 */
export type CurrencyCode = 'USD' | 'GBP'

/**
 * Available currency configurations
 */
export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    decimalPlaces: 2,
    locale: 'en-US',
    symbolPosition: 'before',
    topupConfig: { presets: [5, 10, 25, 75], min: 5, max: 500, defaultThreshold: 5, defaultAmount: 10 },
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    decimalPlaces: 2,
    locale: 'en-GB',
    symbolPosition: 'before',
    topupConfig: { presets: [5, 10, 25, 75], min: 5, max: 500, defaultThreshold: 5, defaultAmount: 10 },
  },
};

/**
 * Default currency configuration
 * Change this to set the default currency for the application
 */
export const DEFAULT_CURRENCY: CurrencyConfig = CURRENCIES.USD;

/**
 * Get currency configuration by code.
 * Falls back to DEFAULT_CURRENCY for unknown codes and logs a warning.
 */
export function getCurrencyConfig(code: string): CurrencyConfig {
  const config = CURRENCIES[code.toUpperCase() as CurrencyCode]
  if (!config) {
    console.warn(`[currency] Unknown currency code "${code}", falling back to ${DEFAULT_CURRENCY.code}`)
    return DEFAULT_CURRENCY
  }
  return config
}
